import type { IUserData } from "../shared/session/interfaces/session";
import type {
  IEstimateOption,
  IPreQuoteRequest,
  QuoteAnchor,
} from "../services/api";

/**
 * Catalog funding methods (UPPER_SNAKE). Mirrors the canonical list in
 * `raas-widgets-backend/src/catalog/constants/catalog.constants.ts`.
 */
export const FUNDING_METHODS = [
  "DEBIT_CARD",
  "CREDIT_CARD",
  "ACH",
  "RTP",
  "WALLET",
  "CASH_DEPOSIT",
  "BANK_BALANCE",
] as const;
export type FundingMethod = (typeof FUNDING_METHODS)[number];

/**
 * Maps catalog funding method codes to the UI category used by the
 * payment-method selector: CARD | ACCOUNT | CASH | OTHER.
 */
export type FundingCategory = "CARD" | "ACCOUNT" | "CASH" | "OTHER";

export const FUNDING_METHOD_CATEGORY: Record<FundingMethod, FundingCategory> = {
  DEBIT_CARD: "CARD",
  CREDIT_CARD: "CARD",
  ACH: "ACCOUNT",
  RTP: "ACCOUNT",
  BANK_BALANCE: "ACCOUNT",
  WALLET: "OTHER",
  CASH_DEPOSIT: "CASH",
};

/**
 * Classify any funding method string into a UI category. Accepts both the
 * canonical catalog vocabulary and the legacy lowercase trio
 * (`card` / `bank_account` / `cash`).
 */
export function categoryFromFundingMethod(
  method: string | undefined | null
): FundingCategory | undefined {
  if (!method) return undefined;
  const upper = method.toUpperCase();
  if (upper in FUNDING_METHOD_CATEGORY) {
    return FUNDING_METHOD_CATEGORY[upper as FundingMethod];
  }
  switch (method) {
    case "card":
      return "CARD";
    case "bank_account":
      return "ACCOUNT";
    case "cash":
      return "CASH";
    default:
      return undefined;
  }
}

/**
 * Maps the widget's payment type (`CARD` | `BANK` | `CASH`) to a UI funding
 * category, so a selected payment method can be matched against a pre-quote SKU.
 */
export function categoryFromPaymentType(
  type: string | undefined | null
): FundingCategory | undefined {
  switch (type) {
    case "CARD":
      return "CARD";
    case "BANK":
      return "ACCOUNT";
    case "CASH":
      return "CASH";
    default:
      return undefined;
  }
}

/**
 * Resolves the canonical payout method (UPPER_SNAKE) the transfer must be
 * priced against. The payout is fixed by the claim link, so we prefer the
 * explicit `userData.paymentMethod.payoutMethod` and fall back to mapping the
 * legacy `type` discriminator.
 */
export function resolvePayoutMethod(
  userData?: IUserData | null
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.payoutMethod) return pm.payoutMethod;
  switch (pm.type) {
    case "bank_account":
      return "BANK_ACCOUNT";
    case "cash":
      return "CASH_PICKUP";
    case "card":
      return "PUSH_TO_CARD";
    case "mobile_wallet":
      return "MOBILE_WALLET";
    case "home_delivery":
      return "HOME_DELIVERY";
    default:
      return undefined;
  }
}

/**
 * Resolves the canonical payer code the transfer is locked to (e.g. `ELEKTRA`
 * for cash pickup, `BBVA` for a bank account, carrier for mobile wallet), or
 * `undefined` when the claim does not pin a specific payer.
 */
export function resolvePayerCode(
  userData?: IUserData | null
): string | undefined {
  const pm = userData?.paymentMethod;
  if (!pm) return undefined;
  if (pm.type === "cash" || pm.payoutMethod === "CASH_PICKUP") {
    return pm.cash?.payer ?? pm.cash?.provider;
  }
  if (pm.type === "bank_account" || pm.payoutMethod === "BANK_ACCOUNT") {
    return pm.bankAccount?.payer ?? pm.bankAccount?.bankCode;
  }
  if (pm.type === "mobile_wallet" || pm.payoutMethod === "MOBILE_WALLET") {
    return pm.mobileWallet?.carrier;
  }
  return undefined;
}

/**
 * Normalize a cash-network payer code for cross-corridor matching.
 * CRB publishes `MX_ELEKTRA`; INCOMM publishes `ELEKTRA` for the same network.
 */
export function normalizePayerCode(code: string | undefined | null): string {
  if (!code?.trim()) return "";
  let upper = code.trim().toUpperCase();
  // Strip ISO country prefix when present (`MX_ELEKTRA` → `ELEKTRA`).
  if (/^[A-Z]{2}_/.test(upper)) {
    upper = upper.slice(3);
  }
  // Collapse common catalog suffixes that differ by corridor naming.
  return upper
    .replace(/_WALMART$/, "")
    .replace(/_CASH$/, "")
    .replace(/^BODEGA_AURRERA$/, "BODEGA_AURRERA")
    .replace(/^WALMART_BODEGA_AURRERA$/, "BODEGA_AURRERA");
}

/**
 * True when two payer codes refer to the same receive network across corridors
 * (exact match or normalized equivalence, e.g. `ELEKTRA` ≡ `MX_ELEKTRA`).
 */
export function payerCodesEquivalent(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  const left = a.trim().toUpperCase();
  const right = b.trim().toUpperCase();
  if (left === right) return true;
  return normalizePayerCode(left) === normalizePayerCode(right);
}

/** Canonical catalog `displayName`, or null until the list can resolve it. */
export function catalogPayerDisplayName(
  code: string | null | undefined,
  payers:
    | Array<{ payerCode: string; displayName?: string }>
    | null
    | undefined
): string | null {
  if (!code?.trim() || !payers) return null;
  const found = payers.find((p) => payerCodesEquivalent(p.payerCode, code));
  const name = found?.displayName?.trim();
  return name ? name : null;
}

/**
 * Pre-quote body for fund-request claim: payout (+ payer) pinned, fan out
 * funding methods. `payerCode` must be sent so the engine collapses cheapest
 * per funding *within* the pinned network — not across all cash payers.
 */
export function buildClaimFundingPreQuoteInput(params: {
  amount: number;
  amountAnchor: QuoteAnchor;
  originCountryCode: string;
  destinationCountryCode: string;
  sourceCurrency: string;
  destinationCurrency: string;
  payoutMethod?: string;
  payerCode?: string;
}): IPreQuoteRequest {
  const {
    amount,
    amountAnchor,
    originCountryCode,
    destinationCountryCode,
    sourceCurrency,
    destinationCurrency,
    payoutMethod,
    payerCode,
  } = params;
  return {
    originCountryCode,
    destinationCountryCode,
    amount,
    anchor: amountAnchor,
    sourceCurrency,
    destinationCurrency,
    ...(payoutMethod ? { payoutMethod } : {}),
    ...(payerCode ? { payerCode } : {}),
    vary: "fundingMethod",
    include: ["facets", "options", "summary"],
  };
}

/**
 * Pre-quote body for the payout picker: nothing pinned, one option per payer.
 * `vary: "fundingMethod"` would collapse the response to one option per funding
 * method (a single payer each), hiding every other payer the corridor offers.
 */
export function buildPayoutDiscoveryPreQuoteInput(params: {
  amount: number;
  amountAnchor: QuoteAnchor;
  originCountryCode: string;
  destinationCountryCode: string;
  sourceCurrency: string;
  destinationCurrency: string;
}): IPreQuoteRequest {
  return {
    originCountryCode: params.originCountryCode,
    destinationCountryCode: params.destinationCountryCode,
    amount: params.amount,
    anchor: params.amountAnchor,
    sourceCurrency: params.sourceCurrency,
    destinationCurrency: params.destinationCurrency,
    vary: "payer",
    include: ["facets", "options", "summary"],
  };
}

/**
 * Narrows a pre-quote's options to the claim's fixed payout (+ payer when
 * pinned). Payer matching accepts cross-corridor aliases so a claim minted
 * with `MX_ELEKTRA` still surfaces INCOMM `ELEKTRA` + `CASH_DEPOSIT` options.
 * When a payer is pinned and nothing matches (even via alias), the result is
 * empty — UI must show route unavailable.
 */
export function filterPreQuoteOptions(
  options: IEstimateOption[] | undefined | null,
  payoutMethod: string | undefined,
  payerCode: string | undefined
): IEstimateOption[] {
  let result = options ?? [];
  if (payoutMethod) {
    result = result.filter((o) => o.payoutMethod === payoutMethod);
  }
  if (payerCode) {
    result = result.filter((o) =>
      payerCodesEquivalent(o.payerCode, payerCode)
    );
  }
  return result;
}

/**
 * Distinct UI funding categories (CARD / ACCOUNT / CASH / OTHER) available
 * across a set of (already filtered) pre-quote options.
 */
export function availableFundingCategories(
  options: IEstimateOption[]
): Set<FundingCategory> {
  return new Set(
    options
      .map((o) => categoryFromFundingMethod(o.fundingMethod))
      .filter((c): c is FundingCategory => Boolean(c))
  );
}

/**
 * Picks the cheapest (lowest `sendAmount`) option whose funding method maps to
 * the given UI category, from an already-filtered option list.
 */
export function optionForCategory(
  options: IEstimateOption[],
  category: FundingCategory
): IEstimateOption | undefined {
  return options
    .filter((o) => categoryFromFundingMethod(o.fundingMethod) === category)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0];
}

export function isFundingMethod(
  method: string | null | undefined
): method is FundingMethod {
  return !!method && (FUNDING_METHODS as readonly string[]).includes(method);
}

/**
 * Distinct funding methods available across a set of pre-quote options (or
 * raw funding-method strings from facets), in catalog order.
 */
export function availableFundingMethods(
  optionsOrMethods: Array<Pick<IEstimateOption, "fundingMethod"> | string>
): FundingMethod[] {
  const seen = new Set(
    optionsOrMethods.map((o) =>
      typeof o === "string" ? o.toUpperCase() : o.fundingMethod
    )
  );
  return FUNDING_METHODS.filter((m) => seen.has(m));
}

/**
 * Prefer pre-collapse facets (every quotable method) then options.
 */
export function availableFundingMethodsFromDiscovery(input: {
  facetFundingMethods?: Array<{ fundingMethod: string }> | null;
  options?: IEstimateOption[] | null;
}): FundingMethod[] {
  const fromFacets = (input.facetFundingMethods ?? []).map(
    (f) => f.fundingMethod
  );
  if (fromFacets.length > 0) {
    return availableFundingMethods(fromFacets);
  }
  return availableFundingMethods(input.options ?? []);
}

/**
 * Cheapest option whose funding method matches the given canonical method.
 */
export function optionForFundingMethod(
  options: IEstimateOption[],
  method: FundingMethod
): IEstimateOption | undefined {
  const upper = method.toUpperCase();
  return options
    .filter((o) => (o.fundingMethod ?? "").toUpperCase() === upper)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0];
}

/**
 * Best customerFee for a funding method: facets (pre-collapse) first, then
 * the cheapest matching option. `0` from the engine is kept; missing → null.
 */
export function bestCustomerFeeForFundingMethod(
  method: FundingMethod,
  facets?: {
    fundingMethods?: Array<{
      fundingMethod: string;
      best?: { customerFee?: number | null };
    }>;
  } | null,
  options?: Array<{
    fundingMethod?: string;
    customerFee?: number | null;
    sendAmount: number;
  }> | null
): number | null {
  const target = method.toUpperCase();
  const fromFacet = facets?.fundingMethods?.find(
    (row) => (row.fundingMethod ?? "").toUpperCase() === target
  )?.best?.customerFee;
  if (fromFacet != null) return fromFacet;
  const fromOption = (options ?? [])
    .filter((o) => (o.fundingMethod ?? "").toUpperCase() === target)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0]?.customerFee;
  return fromOption != null ? fromOption : null;
}
